import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { sortRows } from '@/lib/sortTable'
import { cn } from '@/lib/utils'

function SortableHeader({ column, label, className, sortBy, sortDir, onSort, sortable }) {
  if (!sortable) {
    return (
      <th className={cn('px-4 py-3 font-medium', className)}>
        {label}
      </th>
    )
  }
  const active = sortBy === column
  return (
    <th
      className={cn(
        'px-4 py-3 font-medium cursor-pointer select-none hover:text-foreground',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        )}
      </span>
    </th>
  )
}

/**
 * @param {object} props
 * @param {{ id: string, label: string, sortValue?: (row: any) => any, render: (row: any) => React.ReactNode, className?: string }[]} props.columns
 * @param {any[]} props.data
 * @param {{ id: string, dir: 'asc'|'desc' }} [props.defaultSort]
 * @param {string} [props.empty]
 * @param {boolean} [props.loading]
 */
export function SortableTable({
  columns,
  data,
  defaultSort,
  empty = 'No data.',
  loading = false,
  scrollable = false,
}) {
  const firstSortable = columns.find((c) => c.sortValue)?.id ?? columns[0]?.id
  const [sortBy, setSortBy] = useState(defaultSort?.id ?? firstSortable)
  const [sortDir, setSortDir] = useState(defaultSort?.dir ?? 'asc')

  const sorted = useMemo(
    () => sortRows(data, sortBy, sortDir, columns),
    [data, sortBy, sortDir, columns],
  )

  function handleSort(columnId) {
    const col = columns.find((c) => c.id === columnId)
    if (!col?.sortValue) return
    if (sortBy === columnId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(columnId)
      setSortDir('asc')
    }
  }

  if (loading) {
    return <div className="h-48 rounded-lg border border-border bg-muted/20 animate-pulse" />
  }

  if (!data.length) {
    return (
      <div className="rounded-lg border border-border py-8 text-center text-sm text-muted-foreground">
        {empty}
      </div>
    )
  }

  const table = (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
          {columns.map((col) => (
            <SortableHeader
              key={col.id}
              column={col.id}
              label={col.label}
              className={col.className}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
              sortable={Boolean(col.sortValue)}
            />
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row, i) => (
          <tr
            key={[row.atera_id, row.id, row.agent_id, i].filter((v) => v != null && v !== '').join('|')}
            className="border-b border-border/60"
          >
            {columns.map((col) => (
              <td
                key={col.id}
                className={cn(
                  'px-4 py-3',
                  col.wrap === false ? 'whitespace-nowrap' : 'whitespace-normal break-words',
                  col.className,
                )}
              >
                {col.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )

  if (!scrollable) {
    return <div className="rounded-lg border border-border">{table}</div>
  }

  return <HorizontalScrollArea>{table}</HorizontalScrollArea>
}
